/**
 * YD Sequence Model Section
 * 满屏序列帧动画模块 - 91帧时钟动画，纯滚动驱动
 *
 * 功能:
 * - 91帧序列帧加载（AVIF/WebP）
 * - Canvas 渲染（Cover模式，高DPI适配）
 * - 纯滚动驱动播放（ScrollTrigger）
 * - 标题淡出动画
 * - 媒体评价轮播（自动播放 + 指示器）
 * - 背景遮罩淡入效果
 */
(function() {
  'use strict';

  /**
   * 把 "Xsvh" / "Xvh" 转成 "calc(X * var(--locked-vh, 1vh))"，
   * 避开 iOS Safari 地址栏导致的 svh 漂移；非 svh/vh 字符串原样返回。
   */
  function lockVhString(value) {
    if (typeof value !== 'string') return value;
    if (!(value.endsWith('svh') || value.endsWith('vh'))) return value;
    const n = parseFloat(value);
    if (!isFinite(n)) return value;
    return `calc(${n} * var(--locked-vh, 1vh))`;
  }

  /**
   * 序列帧加载器：实现见 assets/yd-sequence-loader.js（全站共用）。
   * 移动端素材帧号 4 位且从 1 开始（frame_0001），桌面端 5 位从 0 开始（1_00000）。
   */
  class SequenceLoader extends window.YDSequenceLoader {
    constructor(config, isMobile = false) {
      const src = isMobile && config.mobile ? config.mobile : config;
      const mobileAssets = isMobile && !!config.mobile;
      super({
        totalFrames: config.totalFrames || 91,
        basePath: src.basePath,
        avifPath: src.avifPath,
        fallbackPath: src.fallbackPath,
        framePad: mobileAssets ? 4 : 5,
        frameStart: isMobile ? 1 : 0,
        // 页面最下方的序列，排在 seq1 / seq2 之后
        priorityBase: 40000,
        progressMode: 'total'
      });
      this.isMobile = isMobile;
    }
  }

  /**
   * Canvas 渲染器
   * 负责高DPI适配和Cover模式渲染
   */
  class CanvasRenderer {
    constructor(canvasId) {
      this.canvas = document.querySelector(canvasId);

      if (!this.canvas) {
        throw new Error(`Canvas element not found: ${canvasId}`);
      }

      this.ctx = this.canvas.getContext('2d');
      this.canvasWidth = 0;
      this.canvasHeight = 0;

      this.resizeCanvas();
      // 200ms debounce 避免移动端地址栏滚动狂触发 resize
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.resizeCanvas(), 200);
      });
    }

    /**
     * 高DPI适配（Retina屏幕）
     */
    resizeCanvas() {
      // 限制 DPR：移动端=1，桌面端≤2，且不超过「源帧宽 / CSS 宽」——位图比源帧更大只会放大
      // clearRect + drawImage 的填充成本，清晰度受源帧限制不会提升
      const isMobile = window.innerWidth < 768;
      const rawDpr = window.devicePixelRatio || 1;
      const rect = this.canvas.getBoundingClientRect();
      let dpr = isMobile ? 1 : Math.min(rawDpr, 2);
      if (this.sourceWidth && rect.width > 0) {
        dpr = Math.min(dpr, Math.max(1, this.sourceWidth / rect.width));
      }

      // 设置Canvas的实际像素尺寸
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);

      // 赋值 width/height 已重置变换矩阵，直接设定缩放
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // 保存逻辑尺寸
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;
    }

    /**
     * Cover 模式渲染
     * 类似 CSS object-fit: cover，保持纵横比铺满
     * @param {HTMLImageElement} img - 图片对象
     */
    renderFrame(img) {
      if (!img) return;

      // 首次得知源帧宽后按上限重算位图
      const srcW = img.naturalWidth || img.width;
      if (srcW && srcW > (this.sourceWidth || 0)) {
        this.sourceWidth = srcW;
        this.resizeCanvas();
      }

      // 清空画布
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;

      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        // 图片更宽 → 按高度铺满，水平居中裁剪
        drawHeight = this.canvasHeight;
        drawWidth = this.canvasHeight * imgAspect;
        drawX = (this.canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        // 图片更高 → 按宽度铺满，垂直居中裁剪
        drawWidth = this.canvasWidth;
        drawHeight = this.canvasWidth / imgAspect;
        drawX = 0;
        drawY = (this.canvasHeight - drawHeight) / 2;
      }

      this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    /**
     * 清空画布
     */
    clear() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }
  }

  /**
   * 滚动动画控制器
   * 负责 ScrollTrigger、帧数计算、标题动画和遮罩控制
   */
  class ScrollAnimationController {
    constructor(config, loader, renderer) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;
      this.scrollTrigger = null;
      this.currentFrame = -1; // 改为 -1 确保首帧渲染

      // 获取标题元素
      this.titleIntro = document.querySelector('#sequence-intro-title');

      // 获取遮罩元素
      this.overlay = document.querySelector('#sequence-overlay');

      // 滚动进度分割点（75%为卡片区域，25%为媒体评价区域）
      this.cardProgressEnd = 0.75;

      // Line Reveal 相关
      this.splitTextInstances = [];  // SplitText 实例
      this.introRevealed = false;    // 是否已触发 Line Reveal
    }

    /**
     * 初始化动画
     */
    initialize() {
      // 1. 渲染第一帧
      this.renderFrame(0);

      // 2. 初始化 SplitText
      this.initSplitText();

      // 3. 初始化标题（显示）并播放 Line Reveal
      if (this.titleIntro) {
        gsap.set(this.titleIntro, { opacity: 1 });
        this.playLineReveal();
      }

      // 4. 创建 ScrollTrigger
      this.createScrollTrigger();

      // 5. 后台加载剩余帧
      this.loader.continuePreload();
    }

    /**
     * 初始化 SplitText - 为标题内的文本元素拆分行
     */
    initSplitText() {
      if (typeof SplitText === 'undefined') {
        return;
      }

      if (!this.titleIntro) return;

      // 获取容器内所有需要拆分的文本元素 (h1, p)
      const textElements = this.titleIntro.querySelectorAll('h1, p');
      const targets = textElements.length > 0 ? textElements : [this.titleIntro];

      targets.forEach(el => {
        // 使用 SplitText 按行拆分
        const split = new SplitText(el, {
          type: 'lines',
          linesClass: 'yd-line'
        });

        // 为每行创建内部 wrapper
        split.lines.forEach(line => {
          const wrapper = document.createElement('div');
          wrapper.className = 'yd-line-inner';
          wrapper.innerHTML = line.innerHTML;
          line.innerHTML = '';
          line.appendChild(wrapper);
        });

        this.splitTextInstances.push(split);

        // 初始化内部元素位置（隐藏）
        gsap.set(el.querySelectorAll('.yd-line-inner'), { yPercent: 100 });
      });
    }

    /**
     * 播放 Line Reveal 动画
     */
    playLineReveal() {
      if (this.introRevealed) return;
      if (!this.titleIntro) return;

      const inners = this.titleIntro.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.introRevealed = true;

      gsap.fromTo(inners,
        { yPercent: 100 },
        {
          yPercent: 0,
          duration: 0.8,
          stagger: 0.12,
          ease: 'power3.out'
        }
      );
    }

    /**
     * 创建 ScrollTrigger
     */
    createScrollTrigger() {
      const scrollHeight = this.config.scrollHeight || '800svh';
      const section = document.querySelector('#sequence-model-section');

      // 给 section 设置最小高度，确保有足够空间 pin
      // 高度 = Canvas 高度 (100svh) + scrollHeight；都用 locked-vh 稳定单位
      if (section) {
        section.style.minHeight = `calc(${lockVhString('100svh')} + ${lockVhString(scrollHeight)})`;
      }

      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#sequence-model-section',
        start: 'top top',
        end: 'bottom bottom',
        pin: '#sequence-model-canvas-wrapper',
        pinSpacing: false,
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress;
          this.handleScrollUpdate(progress);
        },
      });

    }

    /**
     * 处理滚动更新
     * @param {number} progress - 滚动进度（0-1）
     *
     * 进度分配:
     * - 0 ~ 0.75: 卡片区域，序列帧 0→90 帧
     * - 0.75 ~ 1: 媒体评价区域，停在第90帧，遮罩淡入
     */
    handleScrollUpdate(progress) {
      let currentFrame;
      let overlayOpacity = 0;

      if (progress <= this.cardProgressEnd) {
        // 卡片区域：正常播放序列帧
        const frameProgress = progress / this.cardProgressEnd;
        currentFrame = Math.floor(frameProgress * 90);
        overlayOpacity = 0;
      } else {
        // 媒体评价区域：停在最后一帧，遮罩淡入
        currentFrame = 90;
        const overlayProgress = (progress - this.cardProgressEnd) / (1 - this.cardProgressEnd);
        overlayOpacity = overlayProgress * 0.6; // 最大 0.6 透明度
      }

      const clampedFrame = Math.max(0, Math.min(90, currentFrame));

      // 渲染帧
      this.renderFrame(clampedFrame);

      // 更新标题透明度
      this.updateTitleOpacity(clampedFrame);

      // 更新遮罩透明度
      this.updateOverlayOpacity(overlayOpacity);
    }

    /**
     * 更新遮罩透明度
     * @param {number} opacity - 目标透明度（0-1）
     */
    updateOverlayOpacity(opacity) {
      if (!this.overlay) return;
      this.overlay.style.opacity = opacity;
    }

    /**
     * 渲染指定帧
     * @param {number} frameNumber - 帧编号
     */
    renderFrame(frameNumber) {
      // 避免重复渲染同一帧
      if (frameNumber === this.currentFrame) return;

      this.currentFrame = frameNumber;
      const img = this.loader.getFrameSafe(frameNumber);

      if (img) {
        this.renderer.renderFrame(img);
      }
    }

    /**
     * 更新标题透明度（线性淡出）
     * @param {number} currentFrame - 当前帧
     */
    updateTitleOpacity(currentFrame) {
      if (!this.titleIntro) return;

      const fadeOutEnd = 15; // 配置中的 fadeOut.end
      let opacity = 1;

      if (currentFrame >= fadeOutEnd) {
        opacity = 0; // 完全隐藏
      } else if (currentFrame > 0) {
        opacity = 1 - (currentFrame / fadeOutEnd); // 线性淡出
      }

      gsap.set(this.titleIntro, { opacity });
    }

    /**
     * 清理资源
     */
    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }

      this.renderer.clear();
    }
  }

  /**
   * 移动端序列帧控制器（兜底：移动端无 mobile_video 时使用）
   * 序列帧渲染 + 主 pin；标题与 overlay 独立 ScrollTrigger 控制。
   */
  class MobileScrollAnimationController {
    constructor(config, loader, renderer) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;
      this.currentFrame = -1;
      this.triggers = [];

      this.mobileIntroTitle = document.querySelector('#mobile-intro-title');
      this.overlay = document.querySelector('#sequence-overlay');
    }

    initialize() {
      this.renderFrame(0);

      if (this.mobileIntroTitle) {
        gsap.set(this.mobileIntroTitle, { opacity: 1 });
      }

      // 主 pin + 序列帧 scrub（progress 驱动帧渲染）
      this.triggers.push(ScrollTrigger.create({
        trigger: '#sequence-model-section',
        start: 'top top',
        end: 'bottom bottom',
        pin: '#sequence-model-canvas-wrapper',
        pinSpacing: false,
        scrub: 1,
        onUpdate: (self) => {
          const frame = Math.floor(self.progress * 90);
          this.renderFrame(Math.max(0, Math.min(90, frame)));
        }
      }));

      // 公共：intro 标题 fade-out + overlay 黑屏淡入
      setupMobileCommonTriggers(this.triggers, this.mobileIntroTitle, this.overlay);
    }

    renderFrame(frameNumber) {
      if (frameNumber === this.currentFrame) return;
      this.currentFrame = frameNumber;
      const img = this.loader.getFrameSafe(frameNumber);
      if (img) {
        this.renderer.renderFrame(img);
      }
    }

    dispose() {
      this.triggers.forEach(t => t.kill());
      this.triggers = [];
    }
  }

  /**
   * 移动端视频背景控制器
   * 视频循环播放 + 让 canvas-wrapper sticky；标题与 overlay 独立 ScrollTrigger 控制。
   */
  class MobileVideoController {
    constructor(config) {
      this.config = config;
      this.video = document.querySelector('#sequence-model-mobile-video');
      this.mobileIntroTitle = document.querySelector('#mobile-intro-title');
      this.overlay = document.querySelector('#sequence-overlay');
      this.triggers = [];
    }

    initialize() {
      this.playVideo();

      if (this.mobileIntroTitle) {
        gsap.set(this.mobileIntroTitle, { opacity: 1 });
      }

      // 主 pin：让 canvas-wrapper 在整个 section 滚动期间 sticky
      this.triggers.push(ScrollTrigger.create({
        trigger: '#sequence-model-section',
        start: 'top top',
        end: 'bottom bottom',
        pin: '#sequence-model-canvas-wrapper',
        pinSpacing: false
      }));

      // 公共：intro 标题 fade-out + overlay 黑屏淡入
      setupMobileCommonTriggers(this.triggers, this.mobileIntroTitle, this.overlay);
    }

    playVideo() {
      if (!this.video) return;
      const playPromise = this.video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          const playOnInteraction = () => {
            this.video.play();
            document.removeEventListener('touchstart', playOnInteraction);
            document.removeEventListener('click', playOnInteraction);
          };
          document.addEventListener('touchstart', playOnInteraction, { once: true });
          document.addEventListener('click', playOnInteraction, { once: true });
        });
      }
    }

    dispose() {
      this.triggers.forEach(t => t.kill());
      this.triggers = [];
      if (this.video) this.video.pause();
    }
  }

  /**
   * 创建移动端公共 ScrollTrigger（intro 标题 fade-out + overlay 黑屏淡入）
   * 两个移动端 controller 共用。
   *
   * @param {Array<ScrollTrigger>} triggers - controller 自己的 triggers 数组，用于挂载
   * @param {HTMLElement|null} mobileIntroTitle - 移动端 intro 标题元素
   * @param {HTMLElement|null} overlay - 黑屏 overlay 元素
   */
  function setupMobileCommonTriggers(triggers, mobileIntroTitle, overlay) {
    // intro 标题 fade-out（首屏 15% viewport 内淡出）
    triggers.push(ScrollTrigger.create({
      trigger: '#sequence-model-canvas-wrapper',
      start: 'top top',
      end: '+=15%',
      scrub: true,
      onUpdate: (self) => {
        if (mobileIntroTitle) {
          gsap.set(mobileIntroTitle, { opacity: 1 - self.progress });
        }
      }
    }));

    // overlay 黑屏淡入（接近 media-reviews 区时）
    const reviewsEl = document.querySelector('#media-reviews-wrapper');
    if (reviewsEl) {
      triggers.push(ScrollTrigger.create({
        trigger: reviewsEl,
        start: 'top 80%',
        end: 'top 30%',
        scrub: true,
        onUpdate: (self) => {
          if (overlay) {
            overlay.style.opacity = self.progress * 0.6;
          }
        }
      }));
    }
  }

  /**
   * 卡片入场 fade-in（设备无关，两端共用）
   * 6 张 .sequence-feature-card 初始 opacity:0 + y:40，进入视口 85% 时 fade-in（stagger 0.1s）
   */
  function setupCardReveal() {
    const cards = document.querySelectorAll('#sequence-cards-container .sequence-feature-card');
    if (!cards.length) return;

    gsap.set(cards, { opacity: 0, y: 40 });

    ScrollTrigger.batch(cards, {
      start: 'top 85%',
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger: 0.1,
          onComplete() {
            // 释放 inline transform，让 CSS hover 的 transform: scale(1.02) 等生效
            gsap.set(batch, { clearProps: 'y,transform' });
          }
        });
      },
      once: true
    });
  }

  /**
   * 字符显现动画 - 随机闪烁效果
   * @param {Array<HTMLElement>} chars - 字符 span 数组
   * @param {Object} options - 配置选项
   * @returns {gsap.core.Tween} - GSAP 动画实例
   */
  function revealChars(chars, options = {}) {
    const {
      duration = 1.2,
      delay = 0,
      onComplete = null
    } = options;

    return gsap.to(chars, {
      keyframes: [
        { opacity: 1, color: '#333333', duration: 0 },
        { color: '#888888', duration: 0.05 },
        { color: '#444444', duration: 0.05 },
        { color: '#ffffff', duration: 0.2 }
      ],
      stagger: {
        amount: duration,
        from: 'random'
      },
      delay: delay,
      ease: 'power1.out',
      onComplete: onComplete
    });
  }

  /**
   * 字符隐藏动画 - 随机淡出效果
   * @param {Array<HTMLElement>} chars - 字符 span 数组
   * @param {Object} options - 配置选项
   * @returns {gsap.core.Tween} - GSAP 动画实例
   */
  function hideChars(chars, options = {}) {
    const {
      duration = 0.4,
      delay = 0,
      onComplete = null
    } = options;

    return gsap.to(chars, {
      opacity: 0,
      color: '#333333',
      duration: 0.15,
      stagger: {
        amount: duration,
        from: 'random'
      },
      delay: delay,
      ease: 'power1.in',
      onComplete: onComplete
    });
  }

  /**
   * 媒体评价轮播控制器
   * 自动轮播 + 指示器手动切换 + 字符动效
   */
  class MediaReviewsCarousel {
    constructor() {
      this.reviews = document.querySelectorAll('.media-review-card');
      this.indicators = document.querySelectorAll('.media-review-indicator');
      this.currentIndex = 0;
      this.autoPlayInterval = null;
      this.autoPlayDelay = 5000; // 5秒自动切换
      this.pauseTimeout = null;

      // 字符动效相关
      this.charSpans = []; // 存储每个卡片的字符 span 数组
      this.splitInstances = []; // 存储 SplitText 实例（用于清理）
      this.isAnimating = false; // 防止动画重叠
    }

    /**
     * 初始化轮播
     */
    init() {
      if (this.reviews.length === 0) {
        return;
      }

      // 拆分所有评价卡片的文本为字符
      this.splitAllReviewsText();

      // 绑定指示器点击事件
      this.bindIndicators();

      // 播放第一个卡片的入场动画
      this.playInitialReveal();

      // 启动自动轮播（延迟启动，等待入场动画完成）
      setTimeout(() => {
        this.startAutoPlay();
      }, 1500);
    }

    /**
     * 拆分所有评价卡片的文本为字符（使用 GSAP SplitText）
     */
    splitAllReviewsText() {
      if (typeof SplitText === 'undefined') {
        return;
      }

      this.reviews.forEach((review, index) => {
        const textElements = review.querySelectorAll('.review-badge, .review-quote');
        const allChars = [];

        textElements.forEach(el => {
          // 使用 GSAP SplitText 拆分为字符
          const split = new SplitText(el, { type: 'chars' });
          this.splitInstances.push(split);
          allChars.push(...split.chars);
        });

        // 初始化字符样式（隐藏）
        gsap.set(allChars, { opacity: 0, color: '#333333' });

        this.charSpans[index] = allChars;
      });
    }

    /**
     * 播放首个卡片的入场动画
     */
    playInitialReveal() {
      const firstChars = this.charSpans[0];
      if (!firstChars || firstChars.length === 0) return;

      // 确保第一个卡片可见
      gsap.set(this.reviews[0], { opacity: 1 });

      // 播放字符显现动画
      revealChars(firstChars, {
        duration: 1.2,
        delay: 0.2
      });
    }

    /**
     * 绑定指示器点击事件
     */
    bindIndicators() {
      this.indicators.forEach((indicator, index) => {
        indicator.addEventListener('click', () => {
          this.showReview(index);
          // 点击后暂停自动播放，3秒后恢复
          this.pauseAutoPlay(3000);
        });
      });
    }

    /**
     * 显示指定索引的评价
     * @param {number} index - 目标索引
     */
    showReview(index) {
      if (index === this.currentIndex || this.isAnimating) return;

      this.isAnimating = true;

      const currentChars = this.charSpans[this.currentIndex];
      const nextChars = this.charSpans[index];
      const currentReview = this.reviews[this.currentIndex];
      const nextReview = this.reviews[index];
      const oldIndex = this.currentIndex;

      // 更新指示器状态（立即更新）
      this.updateIndicators(index);

      // 更新当前索引
      this.currentIndex = index;

      // 1. 隐藏当前评价的字符
      if (currentChars && currentChars.length > 0) {
        hideChars(currentChars, {
          duration: 0.4,
          onComplete: () => {
            // 隐藏完成后，设置整个卡片不可见
            gsap.set(currentReview, { opacity: 0 });
            currentReview.classList.add('yd-pointer-events-none');
          }
        });
      }

      // 2. 显示新评价的字符（延迟开始，让退出动画先进行一部分）
      nextReview.classList.remove('yd-pointer-events-none');
      gsap.set(nextReview, { opacity: 1 });

      if (nextChars && nextChars.length > 0) {
        // 先重置字符状态
        gsap.set(nextChars, { opacity: 0, color: '#333333' });

        // 延迟播放入场动画
        revealChars(nextChars, {
          duration: 1.0,
          delay: 0.3,
          onComplete: () => {
            this.isAnimating = false;
          }
        });
      } else {
        this.isAnimating = false;
      }
    }

    /**
     * 更新指示器高亮状态
     * @param {number} activeIndex - 当前活动索引
     */
    updateIndicators(activeIndex) {
      this.indicators.forEach((indicator, index) => {
        if (index === activeIndex) {
          indicator.classList.add('active');
          indicator.style.background = '#fff';
        } else {
          indicator.classList.remove('active');
          indicator.style.background = 'rgba(255, 255, 255, 0.3)';
        }
      });
    }

    /**
     * 启动自动轮播
     */
    startAutoPlay() {
      this.stopAutoPlay(); // 先清除已有的定时器

      this.autoPlayInterval = setInterval(() => {
        const nextIndex = (this.currentIndex + 1) % this.reviews.length;
        this.showReview(nextIndex);
      }, this.autoPlayDelay);
    }

    /**
     * 停止自动轮播
     */
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    }

    /**
     * 暂停自动轮播一段时间后恢复
     * @param {number} delay - 暂停时间（毫秒）
     */
    pauseAutoPlay(delay) {
      this.stopAutoPlay();

      if (this.pauseTimeout) {
        clearTimeout(this.pauseTimeout);
      }

      this.pauseTimeout = setTimeout(() => {
        this.startAutoPlay();
      }, delay);
    }

    /**
     * 清理资源
     */
    dispose() {
      this.stopAutoPlay();

      if (this.pauseTimeout) {
        clearTimeout(this.pauseTimeout);
        this.pauseTimeout = null;
      }

      // 清理 SplitText 实例
      this.splitInstances.forEach(split => {
        if (split && split.revert) {
          split.revert();
        }
      });
      this.splitInstances = [];
      this.charSpans = [];
    }
  }

  /**
   * 等待依赖加载
   * @returns {Promise<void>}
   */
  function waitForDependencies() {
    return new Promise((resolve, reject) => {
      const allReady = () =>
        typeof gsap !== 'undefined' &&
        typeof ScrollTrigger !== 'undefined' &&
        typeof window.imageFormatDetector !== 'undefined';

      if (allReady()) {
        resolve();
        return;
      }

      // 优先用 GSAPUtils.waitFor，等 GSAP 就绪后再轮询 imageFormatDetector
      if (window.GSAPUtils && typeof window.GSAPUtils.waitFor === 'function') {
        const timeout = setTimeout(() => reject(new Error('[SequenceModel] 依赖加载超时')), 10000);
        window.GSAPUtils.waitFor(['ScrollTrigger'], () => {
          // GSAP 已就绪，imageFormatDetector 在另一个文件，仍需短轮询
          if (typeof window.imageFormatDetector !== 'undefined') {
            clearTimeout(timeout);
            resolve();
            return;
          }
          const check = setInterval(() => {
            if (typeof window.imageFormatDetector !== 'undefined') {
              clearInterval(check);
              clearTimeout(timeout);
              resolve();
            }
          }, 200);
        });
        return;
      }

      // 回退：轻量轮询（200ms）
      const check = setInterval(() => {
        if (allReady()) {
          clearInterval(check);
          resolve();
        }
      }, 200);

      setTimeout(() => {
        clearInterval(check);
        reject(new Error('[SequenceModel] 依赖加载超时'));
      }, 10000);
    });
  }

  /**
   * 主初始化函数
   */
  async function init() {
    const section = document.querySelector('#sequence-model-section');

    if (!section) {
      return;
    }

    const configData = section.dataset.config;

    if (!configData) {
      return;
    }

    const config = JSON.parse(configData);

    try {
      // 1. 等待依赖（GSAP + ScrollTrigger + imageFormatDetector）
      await waitForDependencies();

      // 卡片入场动画（设备无关，两端共用）
      setupCardReveal();

      // 2. 设备检测 - 根据屏幕宽度选择控制器和帧路径
      const isMobile = window.innerWidth < 768;

      // 3. 移动端: 检查是否有视频元素（优先使用视频）
      const mobileVideo = document.querySelector('#sequence-model-mobile-video');

      if (isMobile && mobileVideo) {
        // 移动端有视频: 使用视频控制器（不加载序列帧，节省流量）
        const mobileController = new MobileVideoController(config);
        mobileController.initialize();

        // 存储移动端实例
        window.ydSequenceModelMobile = {
          controller: mobileController,
          useVideo: true
        };

      } else if (isMobile) {
        // 移动端无视频: 降级使用序列帧控制器
        const loader = new SequenceLoader(config, true);
        await loader.preloadPriority(config.preloadCount || 30);
        const renderer = new CanvasRenderer('#sequence-model-canvas');

        const mobileController = new MobileScrollAnimationController(config, loader, renderer);
        loader.getFocusFrame = () => mobileController.currentFrame;
        mobileController.initialize();

        window.ydSequenceModelMobile = {
          controller: mobileController,
          useVideo: false
        };

        // 后台加载剩余帧
        loader.continuePreload();

      } else {
        // 桌面端: 使用序列帧控制器
        const loader = new SequenceLoader(config, false);
        await loader.preloadPriority(config.preloadCount || 30);
        const renderer = new CanvasRenderer('#sequence-model-canvas');

        const controller = new ScrollAnimationController(config, loader, renderer);
        loader.getFocusFrame = () => controller.currentFrame;
        controller.initialize();

        window.ydSequenceModelController = controller;

        // 后台加载剩余帧
        loader.continuePreload();
      }

      // 4. 创建媒体评价轮播（两端共用）
      const carousel = new MediaReviewsCarousel();
      carousel.init();

      // 5. 存储实例（方便清理）
      window.ydMediaReviewsCarousel = carousel;

    } catch {
      // 初始化失败，静默处理
    }
  }

  /**
   * 页面加载时自动初始化
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /**
   * Shopify Section 卸载时清理资源
   */
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail.sectionId === 'sequence-model-section') {
        // 清理桌面端控制器
        if (window.ydSequenceModelController) {
          window.ydSequenceModelController.dispose();
          window.ydSequenceModelController = null;
        }
        // 清理移动端控制器
        if (window.ydSequenceModelMobile) {
          if (window.ydSequenceModelMobile.controller) {
            window.ydSequenceModelMobile.controller.dispose();
          }
          window.ydSequenceModelMobile = null;
        }
        // 清理媒体评价轮播
        if (window.ydMediaReviewsCarousel) {
          window.ydMediaReviewsCarousel.dispose();
          window.ydMediaReviewsCarousel = null;
        }
      }
    });
  }

})();
